import { map } from 'rxjs/operators'

import { get, post, postWithoutBody, put } from 'utils/rxfetch'

// Vendor Base (IC-49943). Backend contract is camelCase JSON in both bodies and
// query strings (CamelCaseJSONRenderer/Parser + an explicit conversion in the
// list view), so send and read camelCase directly.
// See webapp/rest/urls.py -> settings/vendors.
const BASE = '/members-api/settings/vendors'

// Empty strings, empty arrays and the toolbar's 'all' sentinel are omitted
// rather than sent: `search` is min_length=1 server side and 'all' is not a
// member of AuthStatus, so sending either is a 400 rather than "no filter".
const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  value === 'all' ||
  (Array.isArray(value) && value.length === 0)

// companyFilter rides along as one more parameter. Do not follow
// atoSettings.js's `?companyFilter=` concat helper - it only works for an
// endpoint with no other query params.
const withQuery = (path, params = {}, companyId = '') => {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (isEmpty(value)) return
    if (Array.isArray(value)) value.forEach((item) => search.append(key, item))
    else search.append(key, value)
  })
  if (companyId) search.append('companyFilter', companyId)
  const query = search.toString()
  return query ? `${path}?${query}` : path
}

export default () => ({
  list(params, companyId = '') {
    return get(withQuery(BASE, params, companyId))
  },

  summary(window, companyId = '') {
    return get(withQuery(`${BASE}/summary`, { window }, companyId))
  },

  // Emits the raw ajax response: the caller needs `status` to tell a created
  // vendor (201) from a restored one (200), not just the body.
  create(body, companyId = '') {
    return post(withQuery(BASE, {}, companyId), body)
  },

  remove(vendorIds, companyId = '') {
    return post(withQuery(`${BASE}/remove`, {}, companyId), { vendorIds }).pipe(
      map((x) => x.response)
    )
  },

  restore(vendorIds, companyId = '') {
    return post(withQuery(`${BASE}/restore`, {}, companyId), { vendorIds }).pipe(
      map((x) => x.response)
    )
  },

  // The press answers 202; how it ended comes from syncStatus.
  startSync(companyId = '') {
    return post(withQuery(`${BASE}/sync`, {}, companyId), {}).pipe(map((x) => x.response))
  },

  syncStatus(companyId = '') {
    return get(withQuery(`${BASE}/sync`, {}, companyId))
  },

  autoDetection(companyId = '') {
    return get(withQuery(`${BASE}/auto-detection`, {}, companyId))
  },

  setAutoDetection(enabled, companyId = '') {
    return put(withQuery(`${BASE}/auto-detection`, {}, companyId), {
      autoDetectionEnabled: enabled,
    }).pipe(map((x) => x.response))
  },

  // postWithoutBody is the one helper that leaves Content-Type alone - the
  // browser has to set the multipart boundary itself.
  importFile(file, companyId = '') {
    const form = new FormData()
    form.append('file', file)
    return postWithoutBody(withQuery(`${BASE}/import`, {}, companyId), form).pipe(
      map((x) => x.response)
    )
  },
})
